import React from "react";
import { useSelector } from "react-redux";
import { useTenant } from "../../../provider/TenantProvider";
import { dashboardState } from "../../../redux/reducers/dashboardReducer";
import { userState } from "../../../redux/reducers/userReducer";
import axiosInstance from "../../../utils/axiosInstance";
import { getUrlForBookings } from "../../../utils/confighelpers";
import { apiResult, purchaseName, purchaseStatus } from "../../classes/members/memberUtils";

/**
 * Shared bits of the member portal (Schedule, Club Webshop, My Purchases, My Bookings, My Profile; new design).
 * The member's club is myClubs[0], exactly as the old member pages used it.
 */

export const clubIdOf = (userData) => (userData?.myClubs?.length > 0 && userData.myClubs[0] ? userData.myClubs[0].businessId : "");

/** Club profile for the member's club, from the tenant (club address) data: name, logo, contact details. */
export function useClub() {
  const { tenantData } = useTenant();
  const userReduxState = useSelector(userState);
  const bd = tenantData?.business_details?.business_detail || {};
  const myClub = userReduxState.userData?.myClubs?.[0] || {};
  return {
    id: clubIdOf(userReduxState.userData),
    name: bd.businessName || tenantData?.businessName || myClub.businessName || "your club",
    logo: bd.logo || tenantData?.logo || "",
    workNumber: (bd.workNumber || "").trim(),
    workEmail: (bd.workEmail || "").trim(),
  };
}

let purchaseCache = { key: "", promise: null };

const isUnlimited = (p) => {
  const d = p?.selectedPassDetail || {};
  return d.productType === "subscription" && (d.unlimitedCredits !== false || String(d.noOfAllowedSessions) === "unlimited");
};

/**
 * The member's own purchases at their club (same request as the club's member page:
 * getMyPurchases listOf=free|classpass|subscription&consumerId=me&businessId=club), plus a credits summary.
 * Refreshes when dashboardReduxState.updateSessionComp changes (bumped after a booking).
 */
export function useMemberPurchases() {
  const userReduxState = useSelector(userState);
  const dashboardReduxState = useSelector(dashboardState);
  const me = userReduxState.userData?._id;
  const clubId = clubIdOf(userReduxState.userData);
  const [state, setState] = React.useState({ loading: true, free: [], classpass: [], subscription: [] });

  React.useEffect(() => {
    if (!me || !clubId) return undefined;
    let alive = true;
    const fetchAll = async (listOf) => {
      const out = [];
      for (let page = 1; page <= 10; page++) {
        const res = await axiosInstance({ method: "get", url: `${getUrlForBookings()}getMyPurchases?page=${page}&listOf=${listOf}&consumerId=${me}&businessId=${clubId}` });
        const [ok, data] = apiResult(res);
        const list = ok && Array.isArray(data?.data) ? data.data : [];
        out.push(...list);
        if (!list.length || out.length >= (parseInt(data?.total, 10) || 0)) break;
      }
      return out;
    };
    // The header pill and the page both use this hook: share one set of requests per refresh.
    const key = `${me}|${clubId}|${dashboardReduxState.updateSessionComp}`;
    if (purchaseCache.key !== key) purchaseCache = { key, promise: Promise.all(["free", "classpass", "subscription"].map((k) => fetchAll(k).catch(() => []))) };
    (async () => {
      const [free, classpass, subscription] = (await purchaseCache.promise).map((l) => l.slice());
      const order = (a, b) => purchaseStatus(b).active - purchaseStatus(a).active || new Date(b.purchaseDate || b.createdAt) - new Date(a.purchaseDate || a.createdAt);
      if (alive) setState({ loading: false, free: free.sort(order), classpass: classpass.sort(order), subscription: subscription.sort(order) });
    })();
    return () => {
      alive = false;
    };
  }, [me, clubId, dashboardReduxState.updateSessionComp]);

  const summary = React.useMemo(() => {
    const active = [...state.free, ...state.classpass, ...state.subscription].filter((p) => purchaseStatus(p).active);
    const unlimited = active.find(isUnlimited);
    const counted = active.filter((p) => !isUnlimited(p));
    const credits = counted.reduce((n, p) => n + Math.max(0, parseInt(p.total_voucher_unused, 10) || 0), 0);
    const main = counted.slice().sort((a, b) => (parseInt(b.total_voucher_unused, 10) || 0) - (parseInt(a.total_voucher_unused, 10) || 0))[0];
    return {
      unlimited: !!unlimited,
      unlimitedName: unlimited ? purchaseName(unlimited) : "",
      credits,
      mainName: main ? purchaseName(main) : "",
      ownedProductIds: active.map((p) => String(p.selectedPassDetail?.productId || p.productId || "")).filter(Boolean),
    };
  }, [state]);

  return { ...state, summary };
}
